import * as React from "react";
import { useQuery } from "convex/react";
import type { ColumnDef, ColumnFiltersState } from "@tanstack/react-table";
import { Info } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { api } from "$convex/convexApi";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { BADGE_TONES } from "@/lib/badge-tones";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
  getOrderSequenceBadgeClass,
  getOrderSequenceLabel,
  isDisplayableSequence,
} from "@/lib/customer-tag-colors";
import { formatCurrency, formatDate } from "@/lib/format";
import { getSalesRepBadgeClass } from "@/lib/sales-tag-colors";
import {
  PasswordResetCopyLinkButton,
  PasswordResetEmailButton,
} from "../components/PasswordResetEmailButton";

interface UserRow {
  _id: string;
  userId: string;
  email: string | null;
  name: string | null;
  company: string | null;
  mainPhoneNumber: string | null;
  city: string | null;
  state: string | null;
  tier: number | null;
  paymentTerms: number | null;
  salesRepName: string | null;
  heardAboutUs: string | null;
  heardAboutUsDetail: string | null;
  isEnterprise: boolean;
  acceptsMarketing: boolean;
  totalOrders: number;
  totalSpent: number;
  lastOrderDate: number | null;
  customerSince: number | null;
  createdAt: number;
  sampleContext: {
    hasSamples: boolean;
    count: number;
    latestStatus: string | null;
    latestDate: number | null;
    latestSampleRequestId: string | null;
  };
  followUpContext: {
    openCount: number;
    openFollowUpId: string | null;
    doNotFollowUp: boolean;
    doNotFollowUpReason: string | null;
  };
  accountSearch: string;
  crmStage: CrmAccountStage;
  crmPriority: CrmAccountPriority;
  crmPriorityScore: number;
  lastTouchAt: number | null;
}

const HEARD_ABOUT_US_LABELS: Record<string, string> = {
  google: "Google",
  instagram: "Instagram",
  facebook: "Facebook",
  referral: "Referral",
  other: "Other",
};

type CrmAccountStage =
  | "open_follow_up"
  | "sample_active"
  | "active_customer"
  | "dormant_customer"
  | "prospect"
  | "suppressed";

type CrmAccountPriority = "high" | "medium" | "low" | "suppressed";

const DAY_MS = 24 * 60 * 60 * 1000;
const DORMANT_CUSTOMER_MS = 180 * DAY_MS;
const CRM_PRIORITY_MAX_SCORE = 100;
const CRM_PRIORITY_HIGH_SCORE = 70;
const CRM_PRIORITY_MEDIUM_SCORE = 40;
const SAMPLE_RECENCY_MAX_BOOST = 15;

const CRM_STAGE_OPTIONS: Array<{ value: CrmAccountStage; label: string }> = [
  { value: "open_follow_up", label: "Open follow-up" },
  { value: "sample_active", label: "Sample active" },
  { value: "dormant_customer", label: "Dormant buyer" },
  { value: "active_customer", label: "Active buyer" },
  { value: "prospect", label: "Prospect" },
  { value: "suppressed", label: "Suppressed" },
];

const CRM_PRIORITY_OPTIONS: Array<{ value: CrmAccountPriority; label: string }> = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "suppressed", label: "Suppressed" },
];

const CRM_STAGE_CONFIG: Record<CrmAccountStage, { label: string; tone: string }> = {
  open_follow_up: { label: "Open follow-up", tone: BADGE_TONES.amber },
  sample_active: { label: "Sample active", tone: BADGE_TONES.sky },
  active_customer: { label: "Active buyer", tone: BADGE_TONES.emerald },
  dormant_customer: { label: "Dormant buyer", tone: BADGE_TONES.orange },
  prospect: { label: "Prospect", tone: BADGE_TONES.slate },
  suppressed: { label: "Suppressed", tone: BADGE_TONES.red },
};

const CRM_PRIORITY_CONFIG: Record<CrmAccountPriority, { label: string; tone: string }> = {
  high: { label: "High", tone: BADGE_TONES.red },
  medium: { label: "Medium", tone: BADGE_TONES.amber },
  low: { label: "Low", tone: BADGE_TONES.zinc },
  suppressed: { label: "Suppressed", tone: BADGE_TONES.slate },
};

function formatHeardAboutUsLabel(value: string | null): string | null {
  if (!value) return null;
  return (
    HEARD_ABOUT_US_LABELS[value] ??
    value.replace(/[_-]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase())
  );
}

function formatOptionalDate(value: number | null): string {
  if (!value) return "—";
  return formatDate(value, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function SamplesContextCell({ row }: { row: UserRow }) {
  const sample = row.sampleContext;
  const followUp = row.followUpContext;
  return (
    <div className="min-w-[180px] space-y-1.5">
      <div className="flex flex-wrap items-center gap-1">
        <Badge
          variant="outline"
          className={sample.hasSamples ? BADGE_TONES.sky : BADGE_TONES.zinc}
        >
          {sample.hasSamples ? `${sample.count} sample${sample.count === 1 ? "" : "s"}` : "No samples"}
        </Badge>
        {sample.latestStatus ? (
          <Badge variant="outline" className={BADGE_TONES.slate}>
            {sample.latestStatus}
          </Badge>
        ) : null}
        {followUp.doNotFollowUp ? (
          <Badge variant="outline" className={BADGE_TONES.red}>
            Suppressed
          </Badge>
        ) : null}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {sample.latestDate ? `Latest ${formatOptionalDate(sample.latestDate)}` : "No sample history"}
        {followUp.openCount > 0 ? ` - ${followUp.openCount} open` : ""}
      </p>
      <div className="flex flex-wrap gap-2 text-[11px]">
        <Link className="underline-offset-2 hover:underline" to={`/sales/samples?customerId=${encodeURIComponent(row._id)}`}>
          Samples
        </Link>
        <Link className="underline-offset-2 hover:underline" to={`/sales/crm/follow-ups?customerId=${encodeURIComponent(row._id)}`}>
          Follow-ups
        </Link>
      </div>
    </div>
  );
}

function formatPaymentTerms(value: number | null): string {
  if (!value) return "—";
  return `Net ${value}`;
}

function formatLocation(city: string | null, state: string | null): string {
  return [city, state].filter(Boolean).join(", ") || "—";
}

type CrmAccountSignals = Pick<
  UserRow,
  | "_id"
  | "userId"
  | "email"
  | "name"
  | "company"
  | "mainPhoneNumber"
  | "city"
  | "state"
  | "totalOrders"
  | "totalSpent"
  | "lastOrderDate"
  | "customerSince"
  | "createdAt"
  | "sampleContext"
  | "followUpContext"
>;

function latestTimestamp(values: Array<number | null | undefined>): number | null {
  const timestamps = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (timestamps.length === 0) return null;
  return Math.max(...timestamps);
}

function getLastTouchAt(row: CrmAccountSignals): number | null {
  return latestTimestamp([
    row.lastOrderDate,
    row.sampleContext.latestDate,
    row.customerSince,
    row.createdAt,
  ]);
}

function isActionableSampleStatus(status: string | null | undefined): boolean {
  const normalized = status?.trim().toLowerCase() ?? "";
  return normalized !== "rejected" && normalized !== "cancelled" && normalized !== "canceled";
}

export function getCrmStage(row: CrmAccountSignals, now = Date.now()): CrmAccountStage {
  if (row.followUpContext.doNotFollowUp) return "suppressed";
  if (row.followUpContext.openCount > 0) return "open_follow_up";
  if (
    row.sampleContext.hasSamples &&
    row.totalOrders === 0 &&
    isActionableSampleStatus(row.sampleContext.latestStatus)
  ) return "sample_active";

  if (row.totalOrders > 0) {
    const lastCustomerTouchAt = row.lastOrderDate ?? row.customerSince ?? row.createdAt;
    if (lastCustomerTouchAt && now - lastCustomerTouchAt > DORMANT_CUSTOMER_MS) {
      return "dormant_customer";
    }
    return "active_customer";
  }

  return "prospect";
}

function clampCrmPriorityScore(score: number): number {
  if (!Number.isFinite(score)) return 0;
  return Math.max(0, Math.min(CRM_PRIORITY_MAX_SCORE, Math.round(score)));
}

function getSampleRecencyBoost(sampleContext: CrmAccountSignals["sampleContext"], now = Date.now()): number {
  if (!sampleContext.latestDate) return 0;

  const status = sampleContext.latestStatus?.trim().toLowerCase() ?? "";
  if (status === "rejected" || status === "cancelled" || status === "canceled") return 0;

  const ageDays = Math.max(0, Math.floor((now - sampleContext.latestDate) / DAY_MS));
  const recencyBoost = ageDays <= 2 ? 10 : ageDays <= 7 ? 8 : ageDays <= 14 ? 5 : ageDays <= 30 ? 2 : 0;
  const statusBoost =
    status === "delivered"
      ? 5
      : status === "shipped"
        ? 4
        : status === "approved"
          ? 2
          : status === "pending" || status === "requested"
            ? 1
            : 0;

  return Math.min(SAMPLE_RECENCY_MAX_BOOST, recencyBoost + statusBoost);
}

export function getCrmPriorityScore(row: CrmAccountSignals, stage: CrmAccountStage, now = Date.now()): number {
  const revenueBoost = Math.min(Math.floor(row.totalSpent / 1_000), 10);
  const orderBoost = Math.min(row.totalOrders * 2, 10);
  const sampleRecencyBoost = getSampleRecencyBoost(row.sampleContext, now);

  switch (stage) {
    case "open_follow_up":
      return clampCrmPriorityScore(85 + Math.min(row.followUpContext.openCount * 3, 10) + Math.min(revenueBoost, 5) + Math.min(sampleRecencyBoost, 5));
    case "sample_active":
      return clampCrmPriorityScore(65 + Math.min(row.sampleContext.count * 4, 12) + sampleRecencyBoost);
    case "dormant_customer":
      return clampCrmPriorityScore(65 + revenueBoost + orderBoost + Math.min(sampleRecencyBoost, 5));
    case "active_customer":
      return clampCrmPriorityScore(45 + revenueBoost + orderBoost + Math.min(sampleRecencyBoost, 5));
    case "prospect":
      return clampCrmPriorityScore(20 + (row.sampleContext.count > 0 ? 10 : 0) + Math.min(sampleRecencyBoost, 5));
    case "suppressed":
      return 0;
  }
}

function getCrmPriority(score: number, stage: CrmAccountStage): CrmAccountPriority {
  if (stage === "suppressed") return "suppressed";
  if (score >= CRM_PRIORITY_HIGH_SCORE) return "high";
  if (score >= CRM_PRIORITY_MEDIUM_SCORE) return "medium";
  return "low";
}

function buildAccountSearch(row: CrmAccountSignals): string {
  return [
    row._id,
    row.userId,
    row.email,
    row.name,
    row.company,
    row.mainPhoneNumber,
    row.city,
    row.state,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function buildCrmAccountFields(row: CrmAccountSignals): Pick<
  UserRow,
  "accountSearch" | "crmStage" | "crmPriority" | "crmPriorityScore" | "lastTouchAt"
> {
  const now = Date.now();
  const crmStage = getCrmStage(row, now);
  const crmPriorityScore = getCrmPriorityScore(row, crmStage, now);

  return {
    accountSearch: buildAccountSearch(row),
    crmStage,
    crmPriority: getCrmPriority(crmPriorityScore, crmStage),
    crmPriorityScore,
    lastTouchAt: getLastTouchAt(row),
  };
}

function matchesSelectedFilter(filterValue: unknown, value: string | boolean | number | null | undefined): boolean {
  const selectedValues = Array.isArray(filterValue) ? filterValue : [filterValue];
  const normalizedValue = String(value ?? "");
  return selectedValues.some((selected) => String(selected) === normalizedValue);
}

function matchesMinimumFilter(value: number, filterValue: unknown): boolean {
  const rawValue = Array.isArray(filterValue) ? filterValue[0] : filterValue;
  if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") return true;

  const minimum = Number(rawValue);
  if (!Number.isFinite(minimum)) return true;
  return value >= minimum;
}

function getCrmPrioritySignals(row: UserRow): string[] {
  const signals = [`Stage: ${CRM_STAGE_CONFIG[row.crmStage].label}`];
  const sampleRecencyBoost = getSampleRecencyBoost(row.sampleContext);

  if (row.followUpContext.openCount > 0) {
    signals.push(`${row.followUpContext.openCount} open follow-up${row.followUpContext.openCount === 1 ? "" : "s"}`);
  }
  if (row.sampleContext.count > 0) {
    signals.push(`${row.sampleContext.count} sample request${row.sampleContext.count === 1 ? "" : "s"}`);
  }
  if (row.sampleContext.latestDate) {
    signals.push(
      row.sampleContext.latestStatus
        ? `Latest sample: ${row.sampleContext.latestStatus} on ${formatOptionalDate(row.sampleContext.latestDate)}`
        : `Latest sample: ${formatOptionalDate(row.sampleContext.latestDate)}`,
    );
  }
  if (sampleRecencyBoost > 0) {
    signals.push(`Recent sample boost: +${sampleRecencyBoost}`);
  }
  if (row.totalOrders > 0) {
    signals.push(`${row.totalOrders} order${row.totalOrders === 1 ? "" : "s"}`);
  }
  if (row.totalSpent > 0) {
    signals.push(`${formatCurrency(row.totalSpent)} lifetime spend`);
  }
  if (row.crmStage === "suppressed" && row.followUpContext.doNotFollowUpReason) {
    signals.push(`Do-not-follow-up reason: ${row.followUpContext.doNotFollowUpReason}`);
  }

  return signals;
}

function CrmPriorityScoreInfo({ row }: { row: UserRow }) {
  const signals = getCrmPrioritySignals(row);

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Explain CRM priority score"
            className="relative inline-flex size-5 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors before:absolute before:-inset-2.5 before:rounded-full before:content-[''] hover:text-foreground focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <Info className="relative z-10 size-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8} className="max-w-[320px] text-left leading-relaxed">
          <div className="space-y-2">
            <div>
              <p className="font-medium">CRM priority score</p>
              <p className="mt-0.5 opacity-90">
                {row.crmPriority === "suppressed"
                  ? "Suppressed accounts are excluded from priority work because they are marked do not follow up."
                  : `This account is scored ${row.crmPriorityScore}/100 for sorting. Recent delivered or shipped samples get extra weight; this is not a true conversion probability.`}
              </p>
            </div>
            <div className="space-y-1">
              <p className="font-medium">Signals used</p>
              <ul className="list-disc space-y-0.5 pl-4">
                {signals.map((signal) => (
                  <li key={signal}>{signal}</li>
                ))}
              </ul>
            </div>
            <p className="border-t border-background/20 pt-2 opacity-90">
              High: {CRM_PRIORITY_HIGH_SCORE}+. Medium: {CRM_PRIORITY_MEDIUM_SCORE}–{CRM_PRIORITY_HIGH_SCORE - 1}. Low: below {CRM_PRIORITY_MEDIUM_SCORE}.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function useColumns(data: UserRow[]): ColumnDef<UserRow>[] {
  const repOptions = React.useMemo(
    () =>
      [...new Set(data.map((row) => row.salesRepName).filter(Boolean))]
        .sort()
        .map((value) => ({ label: value!, value: value! })),
    [data],
  );

  const tierOptions = React.useMemo(
    () =>
      [...new Set(data.map((row) => row.tier).filter((value): value is number => value !== null))]
        .sort((a, b) => a - b)
        .map((value) => ({ label: `Tier ${value}`, value: String(value) })),
    [data],
  );

  const sampleStatusOptions = React.useMemo(
    () =>
      [...new Set(data.map((row) => row.sampleContext.latestStatus).filter(Boolean))]
        .sort()
        .map((value) => ({ label: value!, value: value! })),
    [data],
  );

  const sourceOptions = React.useMemo(
    () =>
      [...new Set(data.map((row) => row.heardAboutUs).filter(Boolean))]
        .sort()
        .map((value) => ({
          label: formatHeardAboutUsLabel(value!) ?? value!,
          value: value!,
        })),
    [data],
  );

  return React.useMemo(() => [
    {
      id: "account",
      accessorFn: (row) => row.accountSearch,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Account" />,
      cell: ({ row }) => {
        const accountName = row.original.company || row.original.name || row.original.email || "Unnamed account";
        const contactName = row.original.company ? row.original.name : null;
        const location = formatLocation(row.original.city, row.original.state);
        const contactLine = [contactName, row.original.email].filter(Boolean).join(" - ") || "No contact";
        return (
          <div className="min-w-[260px] space-y-1">
            <p className="font-medium">{accountName}</p>
            <p className="text-xs text-muted-foreground">{contactLine}</p>
            <div className="flex flex-wrap gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
              {row.original.mainPhoneNumber ? <span>{row.original.mainPhoneNumber}</span> : null}
              {location !== "—" ? <span>{location}</span> : null}
            </div>
          </div>
        );
      },
      meta: { label: "Account", variant: "text" as const, placeholder: "Search account..." },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue: string) => {
        if (!filterValue) return true;
        return row.original.accountSearch.includes(String(filterValue).toLowerCase());
      },
    },
    {
      id: "crmPriority",
      accessorFn: (row) => row.crmPriorityScore,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Priority" />,
      cell: ({ row }) => {
        const config = CRM_PRIORITY_CONFIG[row.original.crmPriority];
        return (
          <div className="min-w-[96px] space-y-1">
            <Badge variant="outline" className={config.tone}>
              {config.label}
            </Badge>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="tabular-nums">
                {row.original.crmPriority === "suppressed" ? "Suppressed" : `${row.original.crmPriorityScore}/100`}
              </span>
              <CrmPriorityScoreInfo row={row.original} />
            </div>
          </div>
        );
      },
      meta: { label: "Priority", variant: "select" as const, options: CRM_PRIORITY_OPTIONS },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesSelectedFilter(filterValue, row.original.crmPriority),
    },
    {
      id: "crmStage",
      accessorKey: "crmStage",
      header: ({ column }) => <DataTableColumnHeader column={column} label="CRM Stage" />,
      cell: ({ row }) => {
        const config = CRM_STAGE_CONFIG[row.original.crmStage];
        const detail =
          row.original.crmStage === "open_follow_up"
            ? `${row.original.followUpContext.openCount} open`
            : row.original.crmStage === "sample_active"
              ? row.original.sampleContext.latestStatus || "Sample history"
              : row.original.crmStage === "suppressed"
                ? row.original.followUpContext.doNotFollowUpReason || "Do not follow up"
                : row.original.totalOrders > 0
                  ? `${row.original.totalOrders} order${row.original.totalOrders === 1 ? "" : "s"}`
                  : "No orders yet";

        return (
          <div className="min-w-[150px] space-y-1">
            <Badge variant="outline" className={config.tone}>
              {config.label}
            </Badge>
            <p className="text-[11px] text-muted-foreground">{detail}</p>
          </div>
        );
      },
      meta: { label: "CRM Stage", variant: "select" as const, options: CRM_STAGE_OPTIONS },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesSelectedFilter(filterValue, row.original.crmStage),
    },
    {
      id: "salesRepName",
      accessorFn: (row) => row.salesRepName?.trim() || undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Rep" />,
      cell: ({ row }) => {
        const rep = row.original.salesRepName;
        if (!rep) return "—";
        return (
          <Badge variant="outline" className={getSalesRepBadgeClass(rep)}>
            {rep}
          </Badge>
        );
      },
      sortUndefined: "last",
      meta: { label: "Rep", variant: "select" as const, options: repOptions },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesSelectedFilter(filterValue, row.original.salesRepName),
    },
    {
      id: "followUps",
      accessorFn: (row) => row.followUpContext.openCount,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Follow-ups" />,
      cell: ({ row }) => {
        const followUp = row.original.followUpContext;
        return (
          <div className="min-w-[128px] space-y-1">
            <Badge
              variant="outline"
              className={followUp.doNotFollowUp ? BADGE_TONES.red : followUp.openCount > 0 ? BADGE_TONES.amber : BADGE_TONES.zinc}
            >
              {followUp.doNotFollowUp ? "Suppressed" : `${followUp.openCount} open`}
            </Badge>
            <Link
              className="block text-[11px] text-muted-foreground underline-offset-2 hover:underline"
              to={`/sales/crm/follow-ups?customerId=${encodeURIComponent(row.original._id)}`}
            >
              Open follow-ups
            </Link>
          </div>
        );
      },
      meta: { label: "Follow-ups", variant: "number" as const, placeholder: "Min open..." },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesMinimumFilter(row.original.followUpContext.openCount, filterValue),
    },
    {
      id: "samples",
      accessorFn: (row) => row.sampleContext.count,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Samples" />,
      cell: ({ row }) => <SamplesContextCell row={row.original} />,
      meta: { label: "Samples", variant: "number" as const, placeholder: "Min samples..." },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesMinimumFilter(row.original.sampleContext.count, filterValue),
    },
    {
      id: "sampleStatus",
      accessorFn: (row) => row.sampleContext.latestStatus ?? undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Sample Status" />,
      cell: ({ row }) => row.original.sampleContext.latestStatus || "—",
      sortUndefined: "last",
      meta: { label: "Sample Status", variant: "select" as const, options: sampleStatusOptions },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesSelectedFilter(filterValue, row.original.sampleContext.latestStatus),
    },
    {
      id: "totalSpent",
      accessorKey: "totalSpent",
      header: ({ column }) => <DataTableColumnHeader column={column} label="Spent" />,
      cell: ({ row }) => formatCurrency(row.original.totalSpent),
      meta: { label: "Spent", variant: "number" as const, placeholder: "Min spent..." },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesMinimumFilter(row.original.totalSpent, filterValue),
    },
    {
      id: "totalOrders",
      accessorKey: "totalOrders",
      header: ({ column }) => <DataTableColumnHeader column={column} label="Orders" />,
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <span className="tabular-nums">{row.original.totalOrders}</span>
          {isDisplayableSequence(row.original.totalOrders) ? (
            <span
              className={`inline-flex items-center rounded border px-1.5 py-0 text-[10px] font-medium ${getOrderSequenceBadgeClass(row.original.totalOrders)}`}
            >
              {getOrderSequenceLabel(row.original.totalOrders)}
            </span>
          ) : null}
        </div>
      ),
      meta: { label: "Orders", variant: "number" as const, placeholder: "Min orders..." },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesMinimumFilter(row.original.totalOrders, filterValue),
    },
    {
      id: "lastTouchAt",
      accessorFn: (row) => row.lastTouchAt ?? undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Last Touch" />,
      cell: ({ row }) => formatOptionalDate(row.original.lastTouchAt),
      sortUndefined: "last",
      meta: { label: "Last Touch", variant: "dateRange" as const },
      enableColumnFilter: true,
    },
    {
      id: "lastOrderDate",
      accessorFn: (row) => row.lastOrderDate ?? undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Last Order" />,
      cell: ({ row }) => formatOptionalDate(row.original.lastOrderDate),
      sortUndefined: "last",
      meta: { label: "Last Order", variant: "dateRange" as const },
      enableColumnFilter: true,
    },
    {
      id: "isEnterprise",
      accessorKey: "isEnterprise",
      header: ({ column }) => <DataTableColumnHeader column={column} label="Type" />,
      cell: ({ row }) => (
        <Badge variant="outline" className={getEnterpriseBadgeClass(row.original.isEnterprise)}>
          {getEnterpriseBadgeLabel(row.original.isEnterprise)}
        </Badge>
      ),
      meta: {
        label: "Type",
        variant: "select" as const,
        options: [
          { label: "Enterprise", value: "true" },
          { label: "Standard", value: "false" },
        ],
      },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesSelectedFilter(filterValue, Boolean(row.original.isEnterprise)),
    },
    {
      id: "tier",
      accessorFn: (row) => row.tier ?? undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Tier" />,
      cell: ({ row }) =>
        row.original.tier ? (
          <Badge variant="outline" className={BADGE_TONES.slate}>
            Tier {row.original.tier}
          </Badge>
        ) : (
          "—"
        ),
      sortUndefined: "last",
      meta: { label: "Tier", variant: "select" as const, options: tierOptions },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesSelectedFilter(filterValue, row.original.tier),
    },
    {
      id: "location",
      accessorFn: (row) => {
        const location = [row.city, row.state].filter(Boolean).join(", ");
        return location || undefined;
      },
      header: ({ column }) => <DataTableColumnHeader column={column} label="Location" />,
      cell: ({ row }) => formatLocation(row.original.city, row.original.state),
      sortUndefined: "last",
      meta: { label: "Location", variant: "text" as const, placeholder: "Search location..." },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue: string) => {
        if (!filterValue) return true;
        const search = String(filterValue).toLowerCase();
        return formatLocation(row.original.city, row.original.state).toLowerCase().includes(search);
      },
    },
    {
      id: "heardAboutUs",
      accessorFn: (row) => row.heardAboutUs?.trim() || undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Source" />,
      cell: ({ row }) => {
        const label = formatHeardAboutUsLabel(row.original.heardAboutUs);
        const detail = row.original.heardAboutUsDetail?.trim() || null;
        if (!label) return "—";
        return (
          <div className="min-w-[120px]">
            <p className="font-medium">{label}</p>
            {detail ? <p className="text-xs text-muted-foreground">{detail}</p> : null}
          </div>
        );
      },
      sortUndefined: "last",
      meta: { label: "Source", variant: "select" as const, options: sourceOptions },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesSelectedFilter(filterValue, row.original.heardAboutUs),
    },
    {
      id: "paymentTerms",
      accessorFn: (row) => row.paymentTerms ?? undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Terms" />,
      cell: ({ row }) => formatPaymentTerms(row.original.paymentTerms),
      sortUndefined: "last",
      meta: { label: "Terms", variant: "text" as const, placeholder: "Search terms..." },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue: string) => {
        if (!filterValue) return true;
        const search = String(filterValue).toLowerCase();
        return formatPaymentTerms(row.original.paymentTerms).toLowerCase().includes(search);
      },
    },
    {
      id: "acceptsMarketing",
      accessorKey: "acceptsMarketing",
      header: ({ column }) => <DataTableColumnHeader column={column} label="Marketing" />,
      cell: ({ row }) => {
        const acceptsMarketing = row.original.acceptsMarketing;
        return (
          <Badge
            variant="outline"
            className={acceptsMarketing ? BADGE_TONES.emerald : BADGE_TONES.zinc}
          >
            {acceptsMarketing ? "Opted In" : "Opted Out"}
          </Badge>
        );
      },
      meta: {
        label: "Marketing",
        variant: "select" as const,
        options: [
          { label: "Opted In", value: "true" },
          { label: "Opted Out", value: "false" },
        ],
      },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) =>
        matchesSelectedFilter(filterValue, Boolean(row.original.acceptsMarketing)),
    },
    {
      id: "customerSince",
      accessorFn: (row) => row.customerSince ?? undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Customer Since" />,
      cell: ({ row }) => formatOptionalDate(row.original.customerSince),
      sortUndefined: "last",
      meta: { label: "Customer Since", variant: "dateRange" as const },
      enableColumnFilter: true,
    },
    {
      id: "createdAt",
      accessorKey: "createdAt",
      header: ({ column }) => <DataTableColumnHeader column={column} label="Joined" />,
      cell: ({ row }) => formatOptionalDate(row.original.createdAt),
      meta: { label: "Joined", variant: "dateRange" as const },
      enableColumnFilter: true,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-1">
          <PasswordResetEmailButton
            email={row.original.email}
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
          />
          <PasswordResetCopyLinkButton
            email={row.original.email}
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
          />
        </div>
      ),
      meta: { label: "Actions" },
      enableColumnFilter: false,
      enableSorting: false,
    },
  ], [repOptions, sampleStatusOptions, sourceOptions, tierOptions]);
}

function StatCard({
  title,
  value,
  description,
}: {
  title: string;
  value: string | number;
  description: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

export default function SalesUsersPage() {
  const { authUserId } = useAuthIdentity();
  const [searchParams] = useSearchParams();
  const emailFilter = searchParams.get("email")?.trim() ?? "";
  const userIdFilter = searchParams.get("userId")?.trim() ?? "";
  const accountFilter = emailFilter || userIdFilter;

  const insights = useQuery(
    api.marketing.getUsersInsights,
    authUserId
      ? {
          authUserId,
          // No limit - fetch all users for proper filtering
        }
      : "skip",
  );

  const data = React.useMemo<UserRow[]>(() => {
    if (!insights?.users) return [];
    return insights.users.map((user: any) => {
      const row: Omit<UserRow, "accountSearch" | "crmStage" | "crmPriority" | "crmPriorityScore" | "lastTouchAt"> = {
        _id: String(user._id),
        userId: String(user.userId ?? user._id),
        email: user.email ?? null,
        name: user.name ?? null,
        company: user.company ?? null,
        mainPhoneNumber: user.mainPhoneNumber ?? null,
        city: user.city ?? null,
        state: user.state ?? null,
        tier: user.tier ?? null,
        paymentTerms: user.paymentTerms ?? null,
        salesRepName: user.salesRepName ?? null,
        heardAboutUs: user.heardAboutUs ?? null,
        heardAboutUsDetail: user.heardAboutUsDetail ?? null,
        isEnterprise: Boolean(user.isEnterprise),
        acceptsMarketing: Boolean(user.acceptsMarketing),
        totalOrders: Number(user.totalOrders ?? 0),
        totalSpent: Number(user.totalSpent ?? 0),
        lastOrderDate: user.lastOrderDate ?? null,
        customerSince: user.customerSince ?? null,
        createdAt: Number(user.createdAt ?? Date.now()),
        sampleContext: user.sampleContext ?? {
          hasSamples: false,
          count: 0,
          latestStatus: null,
          latestDate: null,
          latestSampleRequestId: null,
        },
        followUpContext: user.followUpContext ?? {
          openCount: 0,
          openFollowUpId: null,
          doNotFollowUp: false,
          doNotFollowUpReason: null,
        },
      };

      return {
        ...row,
        ...buildCrmAccountFields(row),
      };
    });
  }, [insights?.users]);

  const columns = useColumns(data);
  const initialColumnFilters = React.useMemo<ColumnFiltersState>(() => {
    if (!accountFilter) return [];
    return [{ id: "account", value: accountFilter }];
  }, [accountFilter]);

  const crmStats = React.useMemo(() => {
    return {
      totalAccounts: data.length,
      highPriority: data.filter((row) => row.crmPriority === "high").length,
      openFollowUps: data.filter((row) => row.followUpContext.openCount > 0).length,
      sampleActive: data.filter((row) => row.crmStage === "sample_active").length,
      dormantBuyers: data.filter((row) => row.crmStage === "dormant_customer").length,
    };
  }, [data]);

  const loading = insights === undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Sales CRM • People</h1>
        <p className="mt-1 text-muted-foreground">
          Prioritized account view for follow-ups, samples, reactivation, and buyer context.
        </p>
      </div>

      {insights ? (
        <section className="grid gap-4 md:grid-cols-5">
          <StatCard
            title="Accounts"
            value={crmStats.totalAccounts}
            description="People with account context."
          />
          <StatCard
            title="High Priority"
            value={crmStats.highPriority}
            description="Needs follow-up, sampling, or reactivation."
          />
          <StatCard
            title="Open Follow-ups"
            value={crmStats.openFollowUps}
            description="Accounts with active CRM work."
          />
          <StatCard
            title="Sample Active"
            value={crmStats.sampleActive}
            description="Sampled prospects without orders."
          />
          <StatCard
            title="Dormant Buyers"
            value={crmStats.dormantBuyers}
            description="Customers past 180 days without an order."
          />
        </section>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Accounts</CardTitle>
          <CardDescription>
            {loading
              ? "Loading accounts..."
              : `Showing ${data.length} accounts, sorted by CRM priority by default.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <DataTableSkeleton columnCount={columns.length} rowCount={8} withPagination />
          ) : (
            <InternalEntityDataTable
              key={accountFilter || "all-crm-people"}
              data={data}
              columns={columns}
              pageSize={25}
              initialSorting={[
                { id: "crmPriority", desc: true },
                { id: "lastTouchAt", desc: true },
                { id: "totalSpent", desc: true },
              ]}
              initialColumnFilters={initialColumnFilters}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
